import {match} from 'ts-pattern';

import {isFunction} from 'ramda-adjunct';

import {getRandomId, useQueue} from 'shared/web';

import {AlertVariants} from '../../Alert/Alert';
import {NotificationProps} from '../Notification.types';

function mergeNotificationUpdate(
  existing: NotificationProps,
  update: Partial<NotificationProps>
): NotificationProps {
  const merged = Object.assign({}, existing, update);

  if (merged.layout !== 'detailed') {
    delete merged.isLoading;
    delete merged.actions;
    delete merged.secondaryMessage;
  }

  return merged;
}

interface UseNotificationsStateProps {
  limit: number;
  debug?: boolean;
}

export function useNotificationsState(props: UseNotificationsStateProps) {
  const {state, queue, update, cleanQueue} = useQueue<NotificationProps>({
    initialValues: [],
    limit: props.limit,
  });

  /*
   * create a new notification and add it to the queue
   * */
  const showNotification = (notification: NotificationProps) => {
    const id = notification.id || getRandomId();

    if (props.debug) {
      logFormattedStackTrace(notification.variant);
    }

    update((notifications) => {
      if (notification.id && notifications.some((item) => item.id === notification.id)) {
        return notifications;
      }

      return [...notifications, {...notification, id}];
    });

    return id;
  };

  /*
   *  update a notification in the queue and return the updated notification
   * */
  const updateNotification = (id: string, notification: Partial<NotificationProps>) =>
    update((notifications) => {
      const index = notifications.findIndex((item) => item.id === id);

      if (index === -1) {
        return notifications;
      }

      const newNotifications = [...notifications];
      const existingNotification = newNotifications[index];
      const {id: _ignoreId, ...rest} = notification;

      newNotifications[index] = mergeNotificationUpdate(existingNotification, rest);

      return newNotifications;
    });

  /*
   * remove a notification from the queue and return the updated queue
   * */
  const hideNotification = (id: string) =>
    update((notifications) =>
      notifications.filter((notification) => {
        if (notification.id === id) {
          if (isFunction(notification.onClose)) {
            notification.onClose(notification);
          }
          return false;
        }

        return true;
      })
    );

  /*
   * clean the queue and return the updated queue
   * */
  const clean = () => update(() => []);

  return {
    notifications: state,
    queue,
    showNotification,
    updateNotification,
    hideNotification,
    cleanQueue,
    clean,
  };
}

const logFormattedStackTrace = (notificationVariant: AlertVariants | undefined) => {
  if (!notificationVariant) {
    return;
  }

  const [icon, color] = match(notificationVariant)
    .with('info', () => ['🔵', 'blue'])
    .with('success', () => ['🟢', 'green'])
    .with('warning', () => ['🟡', 'yellow'])
    .with('error', () => ['🔴', 'red'])
    .exhaustive();

  // eslint-disable-next-line no-console
  console.trace(
    `%c ${icon} ${notificationVariant} notification stack trace`,
    'color:' + color + ';font-weight:bold;'
  );
};
